import { combineLatest, forkJoin, interval, of } from 'rxjs';
import {
  delay,
  filter,
  flatMap,
  map,
  share,
  switchMap,
  take,
  throttleTime,
} from 'rxjs/operators';
import {
  MultiPointInstallation,
  Organization,
  CaptureGroup,
} from '../../types/api';
import {
  HighlightableM2MCaptureGroup,
  MultiPointInstallationM2MCapturesAction,
  MultiPointInstallationM2MCapturesActionTypes,
  MultiPointInstallationM2MCapturesState,
} from '../../types/multi-point-installation-m2m-captures';
import {
  RESOURCE_IDLE,
  RESOURCE_LOADING,
  ResourceStatus,
} from '../../types/resource';
import { actions } from '../index';
import { getRequiredSessionToken } from '../../utils/session-token';
import { Toaster } from '@densityco/ui';

import createRxStore, { ofType, rxDispatch } from '..';

import { SenseiApi } from '../../utils/api';
import {
  updateQueryString,
  serializeListViewState,
  deserializeListViewState,
} from '../../utils/rx-store-serialization';
import { updateQueryStringKey } from '../../utils/rx-store-serialization';
import SessionStore from '../session';
import { SessionStatus } from '../../types/session';
import { Filter, FilterOfValueType } from '../../types/filters';
import { Sort, SORT_EMPTY } from '../../types/sort';

export const initialState: MultiPointInstallationM2MCapturesState = {
  page: 1,
  filters: [],
  sort: SORT_EMPTY,
  resource: RESOURCE_IDLE,
};

function reducer(
  state: MultiPointInstallationM2MCapturesState,
  action: MultiPointInstallationM2MCapturesAction,
): MultiPointInstallationM2MCapturesState {
  switch (action.type) {
    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_PAGE:
      console.log('change page action');
      return {
        ...state,
        filters: action.filters,
        sort: action.sort,
        resource: RESOURCE_IDLE,
      };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_LOADING:
      return { ...state, resource: RESOURCE_LOADING };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_LOAD_COMPLETE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            mpi: action.mpi,
            captureGroups: {
              total: action.captureGroups.capture_groups_count,
              total_accuracy: action.captureGroups.total_accuracy,
              groups: action.captureGroups.groups.map((cg) => ({
                ...cg,
                highlighted: false,
              })),
              min_video_starts_at: action.captureGroups.min_video_starts_at,
            },
            selectedCaptureGroupId: action.selectedCaptureGroupId,
            seek: action.seek,

            fetchingMoreMPIs: RESOURCE_IDLE,
          },
        },
      };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_ERROR:
      return {
        ...state,
        resource: { status: ResourceStatus.ERROR, error: action.error },
      };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_FILTERS:
      return { ...state, filters: action.filters };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_SORT:
      return {
        ...state,
        sort: { direction: action.direction, column: action.column },
      };
  }

  if (state.resource.status !== ResourceStatus.COMPLETE) {
    return state;
  }

  switch (action.type) {
    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_SELECT_CAPTURE_GROUP:
      updateQueryStringKey('capture_id', action.id);
      updateQueryStringKey('t', 0);

      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            selectedCaptureGroupId: action.id,
            seek: 0,
          },
        },
      };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_SET_PAGE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            fetchingMoreMPIs: RESOURCE_LOADING,
          },
        },
      };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_NEXT_PAGE_LOAD_COMPLETE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            captureGroups: {
              ...state.resource.data.captureGroups,
              groups: [
                ...state.resource.data.captureGroups.groups,
                ...action.captureGroups.groups,
              ].map((cg) => ({ ...cg, highlighted: false })),
            },

            fetchingMoreMPIs: RESOURCE_IDLE,
          },
        },
      };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_NEXT_PAGE_LOAD_ERROR:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            fetchingMoreMPIs: {
              status: ResourceStatus.ERROR,
              error: action.error,
            },
          },
        },
      };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_SELECTED_CAPTURE_VIEWER_SEEK:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            seek: action.seek,
          },
        },
      };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_PUSH_NEW:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            captureGroups: {
              total: action.totalCaptureGroups,
              total_accuracy: action.totalAccuracy,
              groups: [
                ...action.captureGroups.map((capture) => ({
                  ...capture,
                  highlighted: true,
                })),
                ...state.resource.data.captureGroups.groups,
              ],
              min_video_starts_at: action.minVideoStartsAt,
            },
          },
        },
      };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CLEAR_HIGHLIGHT:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            captureGroups: {
              ...state.resource.data.captureGroups,
              groups: state.resource.data.captureGroups.groups.map((cg) => ({
                ...cg,
                highlighted: false,
              })),
            },
          },
        },
      };

    case MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_DELETE_CAPTURE_GROUPS_COMPLETE:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            captureGroups: {
              ...state.resource.data.captureGroups,
              groups: state.resource.data.captureGroups.groups.filter(
                (i) => !action.ids.includes(i.id),
              ),
            },
          },
        },
      };
    default:
      return state;
  }
}

const MultiPointInstallationM2MCapturesStore = createRxStore(
  'MultiPointInstallationM2MCapturesStore',
  initialState,
  reducer,
);

// Generates query parameters to send to the server when fetching a list of capture groups, based on
// the filters and ordering parameters
function generateParams(
  filters: Array<Filter>,
  sort: Sort,
  page: number = 1,
): { [key: string]: string | number } {
  function getFilterValue(
    filters: Array<Filter>,
    definitionId: string,
    operatorId: string,
    mappingFunction = (filter: Filter): string => `${filter.value}`,
  ): string | undefined {
    const filter = filters.find(
      (f) => f.definitionId === definitionId && f.operatorId === operatorId,
    );
    if (!filter) {
      return undefined;
    }
    return mappingFunction(filter);
  }

  const params = {
    page,

    min_video_starts_at__lt: getFilterValue(
      filters,
      'min_video_starts_at',
      'lt',
    ),
    min_video_starts_at__gt: getFilterValue(
      filters,
      'min_video_starts_at',
      'gt',
    ),
    min_video_starts_at__lte: getFilterValue(
      filters,
      'min_video_starts_at',
      'lte',
    ),
    min_video_starts_at__gte: getFilterValue(
      filters,
      'min_video_starts_at',
      'gte',
    ),

    annotated_events: getFilterValue(filters, 'annotated_events', 'equals'),
    annotated_events__lt: getFilterValue(filters, 'annotated_events', 'lt'),
    annotated_events__gt: getFilterValue(filters, 'annotated_events', 'gt'),
    annotated_events__lte: getFilterValue(filters, 'annotated_events', 'lte'),
    annotated_events__gte: getFilterValue(filters, 'annotated_events', 'gte'),

    total_accuracy: getFilterValue(filters, 'total_accuracy', 'equals'),
    total_accuracy__lt: getFilterValue(filters, 'total_accuracy', 'lt'),
    total_accuracy__gt: getFilterValue(filters, 'total_accuracy', 'gt'),
    total_accuracy__lte: getFilterValue(filters, 'total_accuracy', 'lte'),
    total_accuracy__gte: getFilterValue(filters, 'total_accuracy', 'gte'),
    total_accuracy__isnull: getFilterValue(filters, 'total_accuracy', 'isnull'),

    number_of_captures: getFilterValue(filters, 'number_of_captures', 'equals'),
    number_of_captures__lt: getFilterValue(filters, 'number_of_captures', 'lt'),
    number_of_captures__gt: getFilterValue(filters, 'number_of_captures', 'gt'),
    number_of_captures__lte: getFilterValue(
      filters,
      'number_of_captures',
      'lte',
    ),
    number_of_captures__gte: getFilterValue(
      filters,
      'number_of_captures',
      'gte',
    ),

    is_partial_capture_group: getFilterValue(
      filters,
      'partial_capture_group',
      'equals',
    ),
    is_fully_annotated: getFilterValue(filters, 'fully_annotated', 'equals'),
  };

  if (sort.column !== null && sort.direction !== 'none') {
    params['ordering'] = `${sort.direction === 'desc' ? '' : '-'}${
      sort.column
    }`;
  }

  return params;
}

async function loadNextPage(
  dispatch,
  jwtToken: string,
  organizationId: Organization['id'] | null,
  mpiId: MultiPointInstallation['id'],
  filters: Array<Filter>,
  sort: Sort,
  page: number,
) {
  let captureGroups: any;
  const queryParams = generateParams(filters, sort, page);
  try {
    captureGroups = await SenseiApi.multiPointInstallationM2MCaptureGroups(
      jwtToken,
      mpiId,
      queryParams,
    );
  } catch (error) {
    dispatch({
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_NEXT_PAGE_LOAD_ERROR,
      error,
    });
    return;
  }

  dispatch({
    type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_NEXT_PAGE_LOAD_COMPLETE,
    captureGroups,
  });
}

async function load(
  dispatch,
  jwtToken: string,
  mpiId: MultiPointInstallation['id'],
  mpiCaptureGroupId: CaptureGroup['id'],
  seek: number,
  filters: Array<Filter>,
  sort: Sort,
) {
  dispatch({
    type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_LOADING,
  });
  let mpi: any, captureGroups: any;
  const queryParams = generateParams(filters, sort, 1);
  try {
    [mpi, captureGroups] = await Promise.all([
      SenseiApi.multiPointInstallationDetail(jwtToken, mpiId),
      SenseiApi.multiPointInstallationM2MCaptureGroups(
        jwtToken,
        mpiId,
        queryParams,
      ),
    ]);
  } catch (error) {
    dispatch({
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_ERROR,
      error,
    });
    return;
  }
  let selectedCaptureGroupId = mpiCaptureGroupId;
  if (!mpiCaptureGroupId) {
    // not specified in URL, pick the first one available
    selectedCaptureGroupId =
      captureGroups.groups.length > 0 ? captureGroups.groups[0].id : null;
  }

  if (selectedCaptureGroupId) {
    updateQueryStringKey('capture_id', selectedCaptureGroupId);
  }

  dispatch({
    type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_LOAD_COMPLETE,
    mpi,
    captureGroups,
    selectedCaptureGroupId,
    seek,
  });
}

// =================================
// SIDE EFFECT: on initial load of the list view, load installation and captures.
//              Also, kick off polling the mpi captures endpoint in the background to auto-load
//              new capture groups as they are generated by the backend.
// =================================
const enterPageStream = actions.pipe(
  ofType<{
    id: MultiPointInstallation['id'];
    params: {
      capture_id?: number;
      t?: number;
    };
    filters: Array<Filter>;
    sort: Sort;
  }>(
    MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_PAGE,
  ),
);
console.log('change page');
// Initial data loading
enterPageStream
  .pipe(
    switchMap(
      () => MultiPointInstallationM2MCapturesStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();

    load(
      rxDispatch,
      jwtToken,
      action.id,
      action.params.capture_id,
      action.params.t,
      action.filters,
      action.sort,
    );
  });

// MPI Captures endpoint polling
const MPI_M2M_CAPTURES_POLL_INTERVAL_SECONDS = 30;
const leavePageStream = actions.pipe(
  ofType(
    MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_LEAVE_PAGE,
  ),
);
const newCapturesStream = enterPageStream.pipe(
  switchMap((action) => {
    if (
      action.type ===
      MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_PAGE
    ) {
      return interval(MPI_M2M_CAPTURES_POLL_INTERVAL_SECONDS * 1000).pipe(
        // Every interval "tick", return the initial CHANGE_PAGE action and a snapshot of the state in the store
        flatMap(() =>
          forkJoin(
            of(action),
            MultiPointInstallationM2MCapturesStore.pipe(take(1)),
          ),
        ),
      );
    } else {
      return of();
    }
  }),
  filter(([_, state]) => state.resource.status === ResourceStatus.COMPLETE),
  flatMap(([action, state]) => {
    const jwtToken = getRequiredSessionToken();
    const queryParams = generateParams(state.filters, state.sort, 1);

    return combineLatest(
      of(state),
      SenseiApi.multiPointInstallationM2MCaptureGroups(
        jwtToken,
        action.id,
        queryParams,
      ).catch((error) =>
        Toaster.showToast({ text: 'Error polling MPI', type: 'error' }),
      ),
    );
  }),
  map(
    ([state, capturesResponse]: [
      MultiPointInstallationM2MCapturesState,
      any,
    ]) => {
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        // This should be an impossible case, because of the filter above, but typescript needs this
        return;
      }
      const captureList = state.resource.data.captureGroups.groups;

      // Get all captures that are new, and add them to the start of the captures list
      const existingCaptureIds = new Set(captureList.map((c) => c.id));
      return [
        capturesResponse.total_accuracy,
        capturesResponse.groups.filter(
          (capture) => !existingCaptureIds.has(capture.id),
        ),
        state.resource.data.captureGroups.total,
        state.resource.data.captureGroups.min_video_starts_at,
      ] as [number, Array<HighlightableM2MCaptureGroup>, number, string];
    },
  ),
  filter(
    ([totalAccuracy, captures, totalCaptureGroups, minVideoStartsAt]) =>
      captures.length > 0,
  ),
  share(),
);

newCapturesStream.subscribe(
  ([totalAccuracy, captureGroups, totalCaptureGroups, minVideoStartsAt]) => {
    rxDispatch({
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_PUSH_NEW,
      totalAccuracy,
      captureGroups,
      totalCaptureGroups,
      minVideoStartsAt,
    });
  },
);
// After a certain window, remove the highlight values just so that the data makes more sense.
//
// The ListView component manages all its own animation (in css) so
// this doesn't do anything to trigger the fade out of the highlight
// directly.
newCapturesStream.pipe(delay(3500)).subscribe(() => {
  rxDispatch({
    type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CLEAR_HIGHLIGHT,
  });
});

// =================================
// SIDE EFFECT: when changing the seek time in the capture viewer or changing the filters, update
// the querystring with the correct parameters.
// =================================
actions
  .pipe(
    ofType(
      MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_FILTERS,
      MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_SORT,
      MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_SELECTED_CAPTURE_VIEWER_SEEK,
    ),
    switchMap(() => MultiPointInstallationM2MCapturesStore.pipe(take(1))),
  )
  .subscribe((state) => {
    const jwtToken = getRequiredSessionToken();

    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    load(
      rxDispatch,
      jwtToken,
      state.resource.data.mpi.id,
      state.resource.data.selectedCaptureGroupId,
      state.resource.data.seek,
      state.filters,
      state.sort,
    );

    const querystring = serializeListViewState(
      state.filters,
      '',
      state.sort,

      // Extra page-specific parameters:
      {
        t: deserializeListViewState(location.search.replace(/^\?/, '')).extra.t,
        capture_id: state.resource.data.selectedCaptureGroupId,
      },
    );
    updateQueryString(querystring);
  });

// =================================
// SIDE EFFECT: when a user reaches the end of the mpi captures list page, load more data
// =================================
actions
  .pipe(
    ofType(
      MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_SET_PAGE,
    ),
    switchMap(
      () =>
        combineLatest(
          MultiPointInstallationM2MCapturesStore.pipe(take(1)),
          SessionStore.pipe(take(1)),
        ),
      (action, [mpiInstallationCapturesStore, sessionState]) => ({
        action,
        mpiInstallationCapturesStore,
        sessionState,
      }),
    ),
  )
  .subscribe(({ action, mpiInstallationCapturesStore, sessionState }) => {
    if (sessionState.status !== SessionStatus.LOGGED_IN) {
      return;
    }

    const a = action as any;

    loadNextPage(
      rxDispatch,
      sessionState.token,
      sessionState.selectedOrganizationId,
      a.mpiId,
      mpiInstallationCapturesStore.filters,
      mpiInstallationCapturesStore.sort,
      a.value,
    );
  });

// =================================
// SIDE EFFECT: send a request to delete capture groups when the action is fired
// =================================

actions
  .pipe(
    ofType(
      MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_DELETE_CAPTURE_GROUPS,
    ),
  )
  .subscribe((action) => {
    const jwtToken = getRequiredSessionToken();
    const a = action as any;
    SenseiApi.multiPointInstallationM2MCaptureGroupsDelete(
      jwtToken,
      a.mpiId,
      a.ids,
    )
      .then(() => {
        rxDispatch({
          type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_DELETE_CAPTURE_GROUPS_COMPLETE,
          ids: a.ids,
        });
        Toaster.showToast({
          text:
            a.ids.length === 1
              ? 'Capture group deleted.'
              : 'Capture groups deleted.',
        });
      })
      .catch(() => {
        Toaster.showToast({
          text: 'Error deleting capture groups.',
          type: 'error',
        });
      });
  });

export default MultiPointInstallationM2MCapturesStore;
